import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,Chip,
  CardContent,
  Alert,
  Tooltip,
  IconButton, Skeleton
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import RefreshIcon from '@mui/icons-material/Refresh';
import AndroidIcon from '@mui/icons-material/Adb';
import AppleIcon from '@mui/icons-material/Apple';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const AttendanceSection = ({
  t,
  geolocation,
  disableTimbraButton,
  onGeolocationRefresh,
  onAttendanceClick,
  disableAttendance,
  loading
}) => {
  // Link per attivazione geolocalizzazione
  const androidActivateGeolocationPage =
    "https://support.google.com/nexus/answer/3467281?hl=it#:~:text=Apri%20l%27app%20Impostazioni%20del,disattiva%20Accesso%20alla%20mia%20posizione.";
  const appleActivateGeolocationPage =
    "https://support.apple.com/it-it/HT207092";

  // Se non deve essere mostrato, ritorna null
  if (disableAttendance === "1") return null;
  
  const isInSede = geolocation?.isInSede || false;
  const statusText = geolocation?.statusText || (isInSede ? "In Sede" : "Libero");
  const statusColor = geolocation?.statusColor || (isInSede ? "#38a169" : "#718096");

  return (
    <Paper 
      id="home-attendance" 
      elevation={0} 
      sx={{ 
        mb: 3, mx: 0, width: '100%',
        overflow: 'hidden', 
        borderRadius: '16px', 
        border: '1px solid #e2e8f0', 
        bgcolor: '#f8fafc' ,
        borderLeft: `6px solid ${statusColor}`,
        transition: 'border-color 0.3s ease'
      }}
    >
        {loading ? (
        // SKELETON STATE
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="text" width="50%" height={30} />
          <Skeleton variant="rectangular" width="100%" height={50} sx={{ borderRadius: '12px' }} />
          <Skeleton variant="text" width="80%" height={20} />
        </Box>
      ) : (
        // CONTENT STATE
        <>
      {/* HEADER PRO UNIFORMATO */}
      <Box sx={{ 
        p: 2, 
        bgcolor: '#fff', 
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#1a365d', 
            fontWeight: 800, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            textTransform: 'uppercase',
            letterSpacing: 1
          }}
        >
          <FingerprintIcon />
          {t("presences")}
        </Typography>
        {geolocation?.city && (
      <Chip 
        label={statusText} 
        size="small"
        sx={{ 
          bgcolor: `${statusColor}15`, 
          color: statusColor, 
          fontWeight: 700,
          border: `1px solid ${statusColor}40`,
          fontSize: '0.7rem',
          height: '20px'
        }} 
      />
    )}
        <Tooltip title={t("Aggiorna posizione")}>
          <IconButton 
            onClick={onGeolocationRefresh}
            size="small"
            sx={{ color: '#1a365d' }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <CardContent sx={{ p: 3 }}>
        {/* POSIZIONE RILEVATA */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          {geolocation?.city ? (
            <Typography variant="caption" sx={{ color: '#4a5568', fontWeight: 600, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
              <span style={{ animation: 'pulse 2s infinite' }}>📍</span> {geolocation.city}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.disabled', display: 'block' }}>
              {t("Ricerca posizione in corso...")}
            </Typography>
          )}
          
        </Box>

        {/* BOTTONE TIMBRATURA (Se abilitato) */}
        {geolocation !== undefined && geolocation.enabled && (
          <Button
            variant="contained" 
            disabled={disableTimbraButton}
            fullWidth
            sx={{ 
              bgcolor: isInSede ? "#d69e2e" : "#1a365d",
              color: isInSede ? "#1a202c" : "#fff",
              py: 2, 
              fontSize: "16px",
              fontWeight: "800",
              borderRadius: "12px",
              textTransform: "none",
              boxShadow: '0 10px 20px rgba(26, 54, 93, 0.15)',
              "&:hover": {
                bgcolor: isInSede ? "#b7842a" : "#2c5282", // Giallo più brunito/scuro al passaggio del mouse
                color: isInSede ? "#1a202c" : "#fff",
                transform: 'translateY(-1px)',
                boxShadow: isInSede ? '0 12px 25px rgba(214, 158, 46, 0.3)' : '0 12px 25px rgba(26, 54, 93, 0.25)',
              }
            }}
            onClick={onAttendanceClick}
          >
            {isInSede ? (t("attendanceExit") || "Timbra Uscita") : (t("attendanceEntrance") || t("stamp") || "Timbra Ingresso")}
          </Button>
        )}

        {/* SEZIONE ATTIVAZIONE GEO (Apple / Android) */}
        {(geolocation === undefined || !geolocation.enabled) && (
          <Box sx={{ mb: 1 }}>
            <Alert severity="info" sx={{ mb: 2, borderRadius: '12px', fontSize: '0.85rem' }}>
              {t("geoActivation")}
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Tooltip title={t("attendanceAndroidTooltip")}>
                <IconButton
                  onClick={() => window.open(androidActivateGeolocationPage, "_blank")}
                  sx={{ 
                      bgcolor: '#f0fff4', 
                      border: '1px solid #c6f6d5',
                      '&:hover': { bgcolor: '#c6f6d5' } 
                  }}
                >
                  <AndroidIcon sx={{ color: '#38a169' }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={t("attendanceAppleTooltip")}>
                <IconButton
                  onClick={() => window.open(appleActivateGeolocationPage, "_blank")}
                  sx={{ 
                      bgcolor: '#edf2f7', 
                      border: '1px solid #e2e8f0',
                      '&:hover': { bgcolor: '#e2e8f0' } 
                  }}
                >
                  <AppleIcon sx={{ color: '#2d3748' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* ULTIMA OPERAZIONE */}
        {geolocation !== undefined && geolocation.lastOperation !== null && (
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed #e2e8f0', textAlign: 'center' }}>
             <Typography variant="caption" sx={{ color: '#1a365d', fontWeight: 700, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
               <AccessTimeIcon sx={{ fontSize: 14 }} /> {geolocation.lastOperation}
             </Typography>
          </Box>
        )}
      </CardContent>
      </>
      )}
    </Paper>
  );
};

export default AttendanceSection;